import { SignatoriesCard } from "./SignatoriesCard";
import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Building2, Upload, Trash2, Plus, Loader2, Users, Gauge, FileText, FileSignature, Stamp, Target, Banknote, ListOrdered, ChevronUp, ChevronDown, Fuel, Pencil, Check, X, FolderPlus, RotateCcw, CornerUpLeft, Palette, GripVertical } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { PRESETS, derivePalette, applyTheme, normalizeTheme, DEFAULT_THEME } from "../lib/themes";
import {
  DEFAULT_LAYOUT, normalizeLayout, leafLabel, groupLabel,
  GROUP_ICON_NAMES, resolveGroupIcon,
} from "../lib/navLayout";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Input } from "./ui/input";
import { MuqeemLeaveImport } from "./MuqeemLeaveImport";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { SettingsDialog } from "./SettingsDialog";
import { BioDeviceSettings } from "./BioDeviceSettings";
import { ApprovalFlowsSettings } from "./approvals/ApprovalFlowsSettings";

const FIELD_KEYS = [
  { key: "name" }, { key: "name_en", ltr: true, label: "اسم الشركة (إنجليزي)", ph: "Company name (English)" },
  { key: "cr_number", ltr: true }, { key: "vat_number", ltr: true, label: "الرقم الضريبي (VAT)", ph: "3xxxxxxxxxxxxx3" },
  { key: "phone", ltr: true },
  { key: "email", ltr: true }, { key: "website", ltr: true }, { key: "industry" },
  { key: "employee_count", ltr: true },
];
const SECTOR_KEYS = ["delivery", "retail", "general"];

export const CompanySettingsDialog = ({ open, onOpenChange, onSaved, initialTab, notif }) => {
  const { t, dir, lang } = useLang();
  const { refreshCompany } = useAuth();
  const [tab, setTab] = useState(initialTab || "profile");
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deps, setDeps] = useState([]);
  const [newDep, setNewDep] = useState("");
  const [perf, setPerf] = useState({ min_hours: 8, max_rejected: 3 });
  const [ovr, setOvr] = useState({});
  const [branchNames, setBranchNames] = useState([]);
  const [savingPerf, setSavingPerf] = useState(false);
  const [tgt, setTgt] = useState({ order_target: 0, leave_max_per_month: 4, leave_gap_months: 14 });
  const [tgtOvr, setTgtOvr] = useState({});
  const [leaveOvr, setLeaveOvr] = useState({});
  const [vtOvr, setVtOvr] = useState({});
  const [bvOvr, setBvOvr] = useState({});
  const VEHICLE_TYPES = ["دراجة نارية", "سيارة"];
  const [savingTgt, setSavingTgt] = useState(false);
  const [advRules, setAdvRules] = useState({ debt_check: false, target_check: false, debt_limit: 500 });
  const [riderShowPayableKm, setRiderShowPayableKm] = useState(false);
  const [advSignRequired, setAdvSignRequired] = useState(true);
  const [portalDocUpload, setPortalDocUpload] = useState(true);
  const [navLayout, setNavLayout] = useState(() => normalizeLayout(null));
  const [navLabels, setNavLabels] = useState({});
  const [editKey, setEditKey] = useState(null);
  const [editText, setEditText] = useState("");
  const [savingNav, setSavingNav] = useState(false);
  const [theme, setTheme] = useState(DEFAULT_THEME);
  const [customColor, setCustomColor] = useState("#059669");
  const [savingTheme, setSavingTheme] = useState(false);
  const [policyUploading, setPolicyUploading] = useState(false);
  const logoRef = useRef(null);
  const sigRef = useRef(null);
  const stampRef = useRef(null);
  const [sigUploading, setSigUploading] = useState(false);
  const [stampUploading, setStampUploading] = useState(false);
  const fuelLogoRef = useRef(null);
  const [fuelLogoIdx, setFuelLogoIdx] = useState(null);
  const [fuelUploading, setFuelUploading] = useState(null);
  const policyRef = useRef(null);

  const TL = {
    tabTargets: { ar: "الأهداف والإجازات", en: "Targets & Leaves", ur: "اہداف و چھٹیاں" },
    tabSections: { ar: "ترتيب الأقسام", en: "Sections order", ur: "سیکشنز ترتیب" },
    tabAppearance: { ar: "المظهر والقوالب", en: "Appearance", ur: "ظاہری شکل" },
    appearanceDesc: { ar: "اختر قالب ألوان جاهزاً أو خصّص اللون الأساسي. يُطبَّق فوراً على كامل التطبيق ولجميع مستخدمي الشركة.", en: "Pick a ready color theme or customize the primary color. Applied instantly across the whole app for all company users.", ur: "کلر تھیم منتخب کریں یا پرائمری رنگ حسبِ ضرورت بنائیں۔" },
    customColor: { ar: "لون مخصّص", en: "Custom color", ur: "کسٹم رنگ" },
    sectionsDesc: { ar: "خصّص القائمة الرئيسية بالكامل: أعد تسمية الأقسام والمجموعات، غيّر الترتيب لأعلى/أسفل، ادمج الأقسام داخل مجموعات أو فكّها، وأنشئ مجموعات جديدة باختيار أيقونتها. يُطبّق على جميع مستخدمي الشركة.", en: "Fully customize the main menu: rename sections & groups, reorder up/down, merge sections into groups or ungroup them, and create new groups with an icon. Applies to all company users.", ur: "مینو کو مکمل حسب ضرورت بنائیں: نام تبدیل کریں، ترتیب دیں، گروپ بنائیں/کھولیں۔" },
    saveOrder: { ar: "حفظ الترتيب", en: "Save order", ur: "ترتیب محفوظ کریں" },
    targetTitle: { ar: "التارجت وعدد الإجازات", en: "Order target & leave capacity", ur: "ہدف اور چھٹیاں" },
    targetDesc: { ar: "حدّد عدد الطلبات المستهدف (التارجت) للمندوب وعدد الموظفين المسموح لهم بالإجازة شهرياً. يمكن تخصيص قيمة لكل فرع.", en: "Set the order target per rider and the monthly leave capacity. You can override per branch.", ur: "ہر رائیڈر کا ہدف اور ماہانہ چھٹیوں کی تعداد مقرر کریں۔" },
    orderTarget: { ar: "التارجت (عدد الطلبات المستهدف)", en: "Order target", ur: "ہدف (آرڈرز)" },
    orderTargetHint: { ar: "تحت التارجت يظهر أحمر، عليه أصفر، فوقه أخضر في بطاقة المندوب.", en: "Below target shows red, at target yellow, above target green on the rider card.", ur: "ہدف سے کم سرخ، ہدف پر زرد، ہدف سے زیادہ سبز۔" },
    leaveMax: { ar: "عدد الموظفين المسموح لهم بالإجازة شهرياً", en: "Max employees on leave per month", ur: "ماہانہ چھٹی والے ملازمین کی حد" },
    leaveMaxHint: { ar: "الحد الأقصى لعدد المناديب في إجازة بنفس الشهر لكل فرع.", en: "Maximum riders on leave in the same month per branch.", ur: "فی برانچ ایک ماہ میں زیادہ سے زیادہ رائیڈرز۔" },
    perBranch: { ar: "تخصيص لكل فرع (اترك الخانة فارغة لاستخدام الافتراضي)", en: "Per-branch override (leave empty for default)", ur: "فی برانچ (خالی چھوڑیں تو ڈیفالٹ)" },
    colBranch: { ar: "الفرع", en: "Branch", ur: "برانچ" },
    colTarget: { ar: "التارجت", en: "Target", ur: "ہدف" },
    colLeave: { ar: "عدد الإجازات", en: "Leaves", ur: "چھٹیاں" },
    perVehicle: { ar: "تخصيص التارجت حسب نوع المركبة (اترك فارغاً للافتراضي)", en: "Per-vehicle-type target override (leave empty for default)", ur: "گاڑی کی قسم کے مطابق ہدف" },
    colVehicle: { ar: "نوع المركبة", en: "Vehicle type", ur: "گاڑی کی قسم" },
    vehicleHint: { ar: "تارجت الدراجات النارية يختلف عن السيارات — يُطبّق حسب نوع مركبة الموظف. الأولوية: نوع المركبة ثم الفرع ثم الافتراضي.", en: "Motorcycle target differs from cars — applied by the employee's vehicle type. Precedence: vehicle type > branch > default.", ur: "موٹرسائیکل کا ہدف کاروں سے مختلف۔" },
    perBranchVehicle: { ar: "تخصيص التارجت لكل فرع + نوع مركبة (الأدق)", en: "Per branch + vehicle-type target (most specific)", ur: "برانچ + گاڑی کی قسم کے مطابق ہدف" },
    bvHint: { ar: "الأولوية النهائية: (فرع + نوع مركبة) ثم نوع المركبة ثم الفرع ثم الافتراضي. اترك فارغاً للرجوع للأعلى.", en: "Final precedence: (branch + vehicle) > vehicle > branch > default. Leave empty to fall back.", ur: "ترجیح: برانچ+گاڑی > گاڑی > برانچ > ڈیفالٹ۔" },
    save: { ar: "حفظ الأهداف والإجازات", en: "Save targets & leaves", ur: "محفوظ کریں" },
    advTitle: { ar: "قواعد السلف", en: "Advance rules", ur: "ایڈوانس قواعد" },
    advDesc: { ar: "تحكّم في متى يُسمح للموظف بطلب سلفة من البورتال. عند تفعيل أي قاعدة تفشل، تظهر للموظف رسالة اعتذار توضّح السبب.", en: "Control when an employee can request an advance from the portal. If a rule fails, the employee sees an apology explaining why.", ur: "کنٹرول کریں کہ ملازم کب ایڈوانس مانگ سکتا ہے۔" },
    advDebtSwitch: { ar: "فحص المديونية القائمة", en: "Outstanding debt check", ur: "بقایا قرض چیک" },
    advDebtHint: { ar: "منع طلب سلفة جديدة إذا تجاوزت السلف غير المسددة الحد أدناه.", en: "Block a new advance if unsettled advances exceed the limit below.", ur: "حد سے زیادہ ہونے پر روکیں۔" },
    advDebtLimit: { ar: "حد المديونية المسموح (⃁)", en: "Allowed debt limit (SAR)", ur: "قرض کی حد (SAR)" },
    advTargetSwitch: { ar: "فحص تحقيق التارجت المطلوب", en: "Required target check", ur: "مطلوبہ ہدف چیک" },
    advTargetHint: { ar: "منع طلب سلفة إذا لم يحقق الموظف التارجت المطلوب للفترة الحالية (إجمالي طلباته المكتملة أقل من المطلوب حتى الآن حسب أيام التقرير)، وليس الهدف الشهري الكامل.", en: "Block an advance if the employee hasn't met the required target for the current period (total completed orders below what's required so far based on report days), not the full monthly target.", ur: "موجودہ مدت کا مطلوبہ ہدف پورا نہ ہونے پر روکیں (ماہانہ ہدف نہیں)۔" },    saved: { ar: "تم حفظ الإعدادات", en: "Settings saved", ur: "محفوظ ہو گیا" },
    policyLabel: { ar: "السياسات الداخلية للشركة", en: "Company internal policies", ur: "کمپنی کی داخلی پالیسیاں" },
    policyHint: { ar: "يظهر هذا النص للموظف عند أول دخول ويجب أن يوافق عليه قبل الدخول. يمكنك أيضاً رفع ملف PDF للسياسات.", en: "Shown to the employee on first login; they must accept before entering. You can also upload a PDF.", ur: "پہلی لاگ اِن پر ملازم کو دکھایا جائے گا؛ داخلے سے پہلے قبول کرنا ہوگا۔" },
    policyPh: { ar: "اكتب هنا السياسات الداخلية للشركة…", en: "Write the internal policies here…", ur: "یہاں پالیسیاں لکھیں…" },
    policyPdf: { ar: "رفع ملف السياسات (PDF)", en: "Upload policy PDF", ur: "پالیسی PDF اپلوڈ کریں" },
    policyCurrent: { ar: "الملف الحالي", en: "Current file", ur: "موجودہ فائل" },
    policyUploaded: { ar: "تم رفع ملف السياسات", en: "Policy PDF uploaded", ur: "پالیسی اپلوڈ ہو گئی" },
    riderAppTitle: { ar: "تطبيق المندوب", en: "Rider app", ur: "رائیڈر ایپ" },
    riderAppDesc: { ar: "تحكّم فيما يظهر للمندوب داخل بوابته.", en: "Control what the rider sees inside their portal.", ur: "کنٹرول کریں کہ رائیڈر اپنے پورٹل میں کیا دیکھے۔" },
    riderPayableSwitch: { ar: "إظهار الكيلومترات المستحقة", en: "Show payable kilometers", ur: "قابلِ ادائیگی کلومیٹر دکھائیں" },
    riderPayableHint: { ar: "عند التفعيل، يرى المندوب مجموع كيلومتراته المستحقة لهذا الشهر داخل تبويب أدائه.", en: "When enabled, the rider sees their payable KM total for the current month in their performance tab.", ur: "فعال ہونے پر، رائیڈر اپنے پرفارمنس ٹیب میں اس ماہ کے قابلِ ادائیگی کلومیٹر دیکھے گا۔" },
  };
  const tl = (k) => TL[k][lang] || TL[k].ar;

  const load = async () => {
    try {
      const [c, d] = await Promise.all([api.getCompany(), api.departments()]);
      setCompany(c);
      setDeps(d);
    } catch { toast.error(t("dash.modals.company.toast.loadFail")); }
    try {
      const [s, bn] = await Promise.all([api.getSettings(), api.branchNames()]);
      setPerf({ min_hours: s.perf_min_hours ?? 8, max_rejected: s.perf_max_rejected ?? 3 });
      setOvr(s.perf_branch_overrides || {});
      setTgt({ order_target: s.order_target ?? 0, leave_max_per_month: s.leave_max_per_month ?? 4, leave_gap_months: s.leave_gap_months ?? 14 });
      setTgtOvr(s.order_target_branch_overrides || {});
      setLeaveOvr(s.leave_branch_overrides || {});
      setVtOvr(s.order_target_vehicle_overrides || {});
      setBvOvr(s.order_target_branch_vehicle_overrides || {});
      setAdvRules({
        debt_check: s.advance_debt_check_enabled ?? false,
        target_check: s.advance_target_check_enabled ?? false,
        debt_limit: s.advance_debt_limit ?? 500,
      });
      setRiderShowPayableKm(s.rider_show_payable_km ?? false);
      setAdvSignRequired(s.advance_employee_sign_required !== false);
      setPortalDocUpload(s.portal_doc_upload_enabled !== false);
      setBranchNames(bn || []);
      const cfg = s.nav_config || {};
      setNavLayout(normalizeLayout(cfg.layout));
      setNavLabels(cfg.labels || {});
      const th = normalizeTheme(s.theme);
      setTheme(th);
      setCustomColor(th.palette?.[600] || "#059669");
    } catch { /* ignore — perf tab optional */ }
  };
  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => { if (open) setTab(initialTab || "profile"); }, [open, initialTab]);

  const setNumOvr = (setter) => (branch, val) => {
    setter((o) => {
      const n = { ...o };
      if (val === "" || val === null) delete n[branch];
      else n[branch] = Number(val);
      return n;
    });
  };

  const saveTgt = async () => {
    setSavingTgt(true);
    try {
      await api.updateSettings({
        order_target: Number(tgt.order_target) || 0,
        leave_max_per_month: Number(tgt.leave_max_per_month) || 4,
        leave_gap_months: Number(tgt.leave_gap_months) || 14,
        order_target_branch_overrides: tgtOvr,
        order_target_vehicle_overrides: vtOvr,
        order_target_branch_vehicle_overrides: bvOvr,
        leave_branch_overrides: leaveOvr,
        advance_debt_check_enabled: !!advRules.debt_check,
        advance_employee_sign_required: !!advSignRequired,
        advance_target_check_enabled: !!advRules.target_check,
        advance_debt_limit: Number(advRules.debt_limit) || 0,
        rider_show_payable_km: !!riderShowPayableKm,
        portal_doc_upload_enabled: !!portalDocUpload,
      });
      toast.success(tl("saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setSavingTgt(false); }
  };

  const dragRef = useRef(null);
  const [dragOver, setDragOver] = useState("");
  const placeLeaf = (value, groupId, at) => setNavLayout((prev) => {
    let topAt = at;  // downward drop lands after the target, upward before it
    const next = [];
    prev.forEach((n, idx) => {
      if (n.t === "item" && n.v === value) { if (!groupId && idx > at) topAt = at; return; }
      next.push(n.t === "group" ? { ...n, children: n.children.filter((v) => v !== value) } : n);
    });
    if (!groupId) { next.splice(Math.max(0, Math.min(topAt, next.length)), 0, { t: "item", v: value }); return next; }
    return next.map((n) => {
      if (n.t !== "group" || n.id !== groupId) return n;
      const kids = [...n.children]; kids.splice(at == null ? kids.length : Math.min(at, kids.length), 0, value);
      return { ...n, children: kids };
    });
  });
  const onDrop = (target) => {
    const d = dragRef.current; dragRef.current = null; setDragOver("");
    if (!d) return;
    if (d.kind === "top" && d.node.t === "group") {
      const ti = target.kind === "top" ? target.i : target.gi;
      if (ti == null || ti === d.i) return;
      setNavLayout((o) => { const n = [...o]; const [m] = n.splice(d.i, 1); n.splice(ti, 0, m); return n; });
      return;
    }
    const value = d.kind === "top" ? d.node.v : d.v;
    if (target.kind === "top") placeLeaf(value, null, target.i);
    else if (target.kind === "group") placeLeaf(value, target.id, null);
    else placeLeaf(value, target.id, target.ci);
  };
  const dnd = (key, target, payload) => ({
    draggable: true,
    onDragStart: (e) => { e.stopPropagation(); dragRef.current = payload; e.dataTransfer.effectAllowed = "move"; },
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); if (dragOver !== key) setDragOver(key); },
    onDragLeave: () => setDragOver((k) => (k === key ? "" : k)),
    onDrop: (e) => { e.preventDefault(); e.stopPropagation(); onDrop(target); },
    onDragEnd: () => { dragRef.current = null; setDragOver(""); },
  });
  const moveTop = (i, dir) => setNavLayout((o) => {
    const n = [...o]; const j = i + dir;
    if (j < 0 || j >= n.length) return o;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const moveChild = (groupId, ci, dir) => setNavLayout((o) => o.map((node) => {
    if (node.t !== "group" || node.id !== groupId) return node;
    const kids = [...node.children]; const j = ci + dir;
    if (j < 0 || j >= kids.length) return node;
    [kids[ci], kids[j]] = [kids[j], kids[ci]];
    return { ...node, children: kids };
  }));
  const moveValueToGroup = (value, targetId) => setNavLayout((prev) => {
    const stripped = prev
      .map((n) => (n.t === "group" ? { ...n, children: n.children.filter((v) => v !== value) } : n))
      .filter((n) => !(n.t === "item" && n.v === value));
    if (targetId) return stripped.map((n) => (n.t === "group" && n.id === targetId ? { ...n, children: [...n.children, value] } : n));
    return [...stripped, { t: "item", v: value }];
  });
  const ungroupChild = (groupId, value) => setNavLayout((prev) => {
    const next = prev.map((n) => (n.t === "group" ? { ...n, children: n.children.filter((v) => v !== value) } : n));
    const gi = next.findIndex((n) => n.t === "group" && n.id === groupId);
    next.splice(gi + 1, 0, { t: "item", v: value });
    return next;
  });
  const createGroup = () => setNavLayout((prev) => [
    ...prev, { t: "group", id: `grp_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, icon: "FolderKanban", label: "مجموعة جديدة", children: [] },
  ]);
  const deleteGroup = (groupId) => setNavLayout((prev) => {
    const gi = prev.findIndex((n) => n.t === "group" && n.id === groupId);
    if (gi < 0) return prev;
    const items = prev[gi].children.map((v) => ({ t: "item", v }));
    const next = [...prev]; next.splice(gi, 1, ...items);
    return next;
  });
  const setGroupIcon = (groupId, icon) => setNavLayout((prev) => prev.map((n) => (n.t === "group" && n.id === groupId ? { ...n, icon } : n)));
  const commitRename = () => {
    if (!editKey) return;
    const text = editText.trim();
    if (editKey.startsWith("g:")) {
      const id = editKey.slice(2);
      setNavLayout((prev) => prev.map((n) => (n.t === "group" && n.id === id ? { ...n, label: text } : n)));
    } else {
      const v = editKey.slice(2);
      setNavLabels((l) => { const n = { ...l }; if (text) n[v] = text; else delete n[v]; return n; });
    }
    setEditKey(null); setEditText("");
  };
  const resetNavDefault = () => { setNavLayout(DEFAULT_LAYOUT.map((n) => ({ ...n, children: n.children ? [...n.children] : undefined }))); setNavLabels({}); };

  const saveNav = async () => {
    setSavingNav(true);
    try {
      await api.updateSettings({ nav_config: { layout: navLayout, labels: navLabels } });
      toast.success(tl("saved"));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setSavingNav(false); }
  };
  const selectPreset = (preset) => { const th = { id: preset.id, palette: preset.palette }; setTheme(th); setCustomColor(preset.palette[600]); applyTheme(th); };
  const applyCustom = (hex) => {
    setCustomColor(hex);
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
    const th = { id: "custom", palette: derivePalette(hex) };
    setTheme(th); applyTheme(th);
  };
  const saveTheme = async () => {
    setSavingTheme(true);
    try {
      await api.updateSettings({ theme });
      applyTheme(theme);
      toast.success(tl("saved"));
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setSavingTheme(false); }
  };

  const onPolicyPdf = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setPolicyUploading(true);
    try {
      const res = await api.uploadCompanyPolicy(file);
      setCompany((c) => ({ ...c, policy_pdf_id: res.policy_pdf_id, policy_pdf_name: res.policy_pdf_name }));
      toast.success(tl("policyUploaded"));
    } catch (e2) { toast.error(e2?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setPolicyUploading(false); }
  };

  const setOvrField = (branch, key, val) => {
    setOvr((o) => {
      const next = { ...o };
      const row = { ...(next[branch] || {}) };
      if (val === "" || val === null) delete row[key];
      else row[key] = Number(val);
      if (Object.keys(row).length === 0) delete next[branch];
      else next[branch] = row;
      return next;
    });
  };

  const savePerf = async () => {
    setSavingPerf(true);
    try {
      await api.updateSettings({
        perf_min_hours: Number(perf.min_hours) || 8,
        perf_max_rejected: Number(perf.max_rejected) || 3,
        perf_branch_overrides: ovr,
      });
      toast.success(t("dash.modals.company.toast.perfSaved"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setSavingPerf(false); }
  };

  const set = (k, v) => setCompany((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {}; FIELD_KEYS.forEach((f) => { payload[f.key] = company[f.key] || ""; });
      payload.address = company.address || "";
      payload.sector = company.sector || "delivery";
      payload.portal_ticker_message = company.portal_ticker_message || "";
      payload.policy_text = company.policy_text || "";
      payload.official_name = company.official_name || "";
      payload.official_title = company.official_title || "";
      payload.sig_size = company.sig_size || null;
      payload.stamp_size = company.stamp_size || null;
      payload.fuel_stations = (company.fuel_stations || []).filter((s) => (s.name || "").trim());
      await api.updateCompany(payload);
      refreshCompany?.();
      toast.success(t("dash.modals.company.toast.companySaved"));
      onSaved?.(payload.sector);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.saveFail")); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadCompanyLogo(file);
      setCompany((c) => ({ ...c, logo_id: res.logo_id }));
      refreshCompany?.();
      toast.success(t("dash.modals.company.toast.logoUpdated"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.logoFail")); }
    finally { setUploading(false); }
  };

  const onSignatureUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setSigUploading(true);
    try {
      const res = await api.uploadCompanySignature(file);
      setCompany((c) => ({ ...c, signature_id: res.signature_id }));
      refreshCompany?.();
      toast.success("تم رفع التوقيع");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "تعذّر رفع التوقيع"); }
    finally { setSigUploading(false); }
  };

  const onStampUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setStampUploading(true);
    try {
      const res = await api.uploadCompanyStamp(file);
      setCompany((c) => ({ ...c, stamp_id: res.stamp_id }));
      refreshCompany?.();
      toast.success("تم رفع الختم");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "تعذّر رفع الختم"); }
    finally { setStampUploading(false); }
  };

  const onFuelLogo = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || fuelLogoIdx == null) return;
    const idx = fuelLogoIdx;
    setFuelUploading(idx);
    try {
      const res = await api.uploadCompanyAsset(file);
      const a = [...(company.fuel_stations || [])];
      a[idx] = { ...a[idx], logo_id: res.logo_id };
      set("fuel_stations", a);
      toast.success("تم رفع شعار المحطة");
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر رفع الشعار"); }
    finally { setFuelUploading(null); setFuelLogoIdx(null); }
  };

  const addDep = async () => {
    const name = newDep.trim(); if (!name) return;
    try { const d = await api.createDepartment(name); setDeps((x) => [...x, d]); setNewDep(""); }
    catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.depAddFail")); }
  };
  const removeDep = async (id) => {
    try { await api.deleteDepartment(id); setDeps((x) => x.filter((d) => d.id !== id)); }
    catch (e) { toast.error(e?.response?.data?.detail || t("dash.modals.company.toast.depDelFail")); }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir} className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-y-auto p-4 sm:p-6 gap-3" data-testid="company-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg"><Building2 className="h-5 w-5 text-blue-600" /> {t("dash.modals.company.title")}</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">{t("dash.modals.company.desc")}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} dir={dir}>
          <TabsList data-testid="company-tabs" className="grid grid-cols-4 sm:grid-cols-8 gap-1 h-auto w-full p-1">
            <TabsTrigger value="profile" data-testid="company-tab-profile" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{t("dash.modals.company.tabProfile")}</TabsTrigger>
            <TabsTrigger value="departments" data-testid="company-tab-departments" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{t("dash.modals.company.tabDepartments")}</TabsTrigger>
            <TabsTrigger value="performance" data-testid="company-tab-performance" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{t("dash.modals.company.tabPerformance")}</TabsTrigger>
            <TabsTrigger value="targets" data-testid="company-tab-targets" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{tl("tabTargets")}</TabsTrigger>
            <TabsTrigger value="sections" data-testid="company-tab-sections" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{tl("tabSections")}</TabsTrigger>
            <TabsTrigger value="approvals" data-testid="company-tab-approvals" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">الموافقات</TabsTrigger>
            <TabsTrigger value="appearance" data-testid="company-tab-appearance" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">{tl("tabAppearance")}</TabsTrigger>
            {notif && <TabsTrigger value="notifications" data-testid="company-tab-notifications" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">التنبيهات</TabsTrigger>}
            {notif && <TabsTrigger value="biodevice" data-testid="company-tab-biodevice" className="w-full text-[11px] sm:text-sm px-1.5 py-1.5 whitespace-normal leading-tight">جهاز البصمة</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile" className="mt-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">{t("dash.modals.company.sectorLabel")}</label>
              <p className="text-xs text-slate-400 mb-2">{t("dash.modals.company.sectorHint")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="company-sector-select">
                {SECTOR_KEYS.map((sk) => {
                  const active = (company.sector || "delivery") === sk;
                  return (
                    <button type="button" key={sk} data-testid={`sector-${sk}`}
                      onClick={() => set("sector", sk)}
                      className={`text-start rounded-xl border p-3 transition ${active ? "border-blue-500 ring-2 ring-blue-500/20 bg-blue-50" : "border-slate-200 hover:border-blue-300"}`}>
                      <p className={`font-heading font-bold text-sm ${active ? "text-blue-700" : "text-slate-800"}`}>{t(`dash.modals.company.sectors.${sk}.l`)}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{t(`dash.modals.company.sectors.${sk}.d`)}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl border bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                {company.logo_id
                  ? <img src={fileUrl(company.logo_id)} alt="logo" className="h-full w-full object-contain" />
                  : <Building2 className="h-7 w-7 text-slate-300" />}
              </div>
              <div>
                <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="company-logo-input" />
                <Button variant="outline" size="sm" onClick={() => logoRef.current?.click()} disabled={uploading} data-testid="company-logo-btn">
                  {uploading ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : <Upload className="h-4 w-4 mx-1" />} {t("dash.modals.company.logo")}
                </Button>
                <p className="text-xs text-slate-400 mt-1">{t("dash.modals.company.logoHint")}</p>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="company-signature-section">
              <label className="text-sm font-bold text-emerald-700 flex items-center gap-1.5"><FileText className="h-4 w-4" /> توقيع واسم المسؤول (يظهر في جميع السندات والأوراق)</label>
              <p className="text-xs text-slate-500 mb-2 mt-0.5">اسم وتوقيع المسؤول المعتمد الذي يُطبع في سندات العُهد والمركبات والسُّلَف وحسابات المستأجرين.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">المسمّى الوظيفي</label>
                  <Input className="mt-1" placeholder="المدير التنفيذي" value={company.official_title || ""}
                    data-testid="company-official-title" onChange={(e) => set("official_title", e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">اسم المسؤول</label>
                  <Input className="mt-1" placeholder="اسم المسؤول المفوّض" value={company.official_name || ""}
                    data-testid="company-official-name" onChange={(e) => set("official_name", e.target.value)} />
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className="h-16 w-32 rounded-xl border bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {company.signature_id
                    ? <img src={fileUrl(company.signature_id)} alt="signature" className="h-full w-full object-contain" />
                    : <FileSignature className="h-6 w-6 text-slate-300" />}
                </div>
                <div>
                  <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={onSignatureUpload} data-testid="company-signature-input" />
                  <Button variant="outline" size="sm" onClick={() => sigRef.current?.click()} disabled={sigUploading} data-testid="company-signature-btn">
                    {sigUploading ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : <Upload className="h-4 w-4 mx-1" />} رفع صورة التوقيع
                  </Button>
                  <p className="text-xs text-slate-400 mt-1">PNG بخلفية شفافة يُفضّل. يظهر في جميع السندات.</p>
                </div>
              </div>
              <div className="flex items-center gap-4 mt-3 border-t border-emerald-100 pt-3">
                <div className="h-16 w-32 rounded-xl border bg-white flex items-center justify-center overflow-hidden shrink-0">
                  {company.stamp_id
                    ? <img src={fileUrl(company.stamp_id)} alt="stamp" className="h-full w-full object-contain" />
                    : <Stamp className="h-6 w-6 text-slate-300" />}
                </div>
                <div>
                  <input ref={stampRef} type="file" accept="image/*" className="hidden" onChange={onStampUpload} data-testid="company-stamp-input" />
                  <Button variant="outline" size="sm" onClick={() => stampRef.current?.click()} disabled={stampUploading} data-testid="company-stamp-btn">
                    {stampUploading ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : <Upload className="h-4 w-4 mx-1" />} رفع ختم الشركة
                  </Button>
                  <p className="text-xs text-slate-400 mt-1">ختم الشركة الرسمي — يظهر بجانب التوقيع في السندات.</p>
                </div>
              </div>
              <div className="mt-3 grid gap-4 border-t border-emerald-100 pt-3 sm:grid-cols-2" data-testid="company-sig-sizes">
                {[["sig_size", "حجم التوقيع في الخطابات", 140, 60, 260, "company-sig-size"], ["stamp_size", "حجم الختم في الخطابات", 190, 80, 320, "company-stamp-size"]].map(([k, label, def, min, max, tid]) => (
                  <div key={k}>
                    <div className="flex items-center justify-between text-xs"><span className="font-medium text-slate-700">{label}</span><b className="text-emerald-700" data-testid={`${tid}-val`}>{company[k] || def}px</b></div>
                    <Slider dir="rtl" className="mt-2" min={min} max={max} step={10} value={[company[k] || def]} onValueChange={([v]) => set(k, v)} data-testid={tid} />
                    <div className="mt-2 flex h-20 items-center justify-center overflow-hidden rounded-lg border bg-white">
                      {company[k === "sig_size" ? "signature_id" : "stamp_id"]
                        ? <img src={fileUrl(company[k === "sig_size" ? "signature_id" : "stamp_id"])} alt="" style={{ height: `${Math.round((company[k] || def) * 0.4)}px` }} className="object-contain" />
                        : <span className="text-[11px] text-slate-400">ارفع الصورة أولاً</span>}
                    </div>
                  </div>))}
                <p className="text-[11px] text-slate-400 sm:col-span-2">المعاينة مصغّرة — يُطبّق الحجم على القرارات والخطابات والعقود ومستندات تطبيق المندوب بعد الحفظ.</p>
              </div>
            </div>
            <SignatoriesCard />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELD_KEYS.map((f) => (
                <div key={f.key}>
                  <label className="text-sm font-medium text-slate-700">{f.label || t(`dash.modals.company.fields.${f.key}.l`)}</label>
                  <Input className="mt-1" value={company[f.key] || ""} placeholder={f.ph || t(`dash.modals.company.fields.${f.key}.p`)}
                    dir={f.ltr ? "ltr" : dir} data-testid={`company-${f.key}`}
                    onChange={(e) => set(f.key, e.target.value)} />
                </div>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("dash.modals.company.address")}</label>
              <Textarea className="mt-1" rows={2} value={company.address || ""} placeholder={t("dash.modals.company.addressPh")}
                data-testid="company-address" onChange={(e) => set("address", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">{t("dash.modals.company.tickerLabel")}</label>
              <p className="text-xs text-slate-400 mb-1">{t("dash.modals.company.tickerHint")}</p>
              <Textarea className="mt-1" rows={2} value={company.portal_ticker_message || ""} placeholder={t("dash.modals.company.tickerPh")}
                data-testid="company-ticker-message" onChange={(e) => set("portal_ticker_message", e.target.value)} />
            </div>
            <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-3">
              <label className="text-sm font-bold text-rose-700 flex items-center gap-1.5"><FileText className="h-4 w-4" /> {tl("policyLabel")}</label>
              <p className="text-xs text-slate-500 mb-2 mt-0.5">{tl("policyHint")}</p>
              <Textarea className="mt-1" rows={4} value={company.policy_text || ""} placeholder={tl("policyPh")}
                data-testid="company-policy-text" onChange={(e) => set("policy_text", e.target.value)} />
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <input ref={policyRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPolicyPdf} data-testid="company-policy-input" />
                <Button variant="outline" size="sm" onClick={() => policyRef.current?.click()} disabled={policyUploading} data-testid="company-policy-btn">
                  {policyUploading ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : <Upload className="h-4 w-4 mx-1" />} {tl("policyPdf")}
                </Button>
                {company.policy_pdf_id && (
                  <span className="text-xs text-slate-500 flex items-center gap-1" data-testid="company-policy-current">
                    <FileText className="h-3.5 w-3.5 text-rose-500" /> {tl("policyCurrent")}: {company.policy_pdf_name || "PDF"}
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3" data-testid="company-fuel-stations">
              <label className="text-sm font-bold text-blue-700 flex items-center gap-1.5"><Fuel className="h-4 w-4" /> محطات الوقود (روابط الخرائط)</label>
              <p className="text-xs text-slate-500 mb-2 mt-0.5">أضف اسم المحطة ورابط Google Maps ليظهر للمندوب في «وقودي» ويفتحه للذهاب لأقرب محطة.</p>
              <div className="space-y-2">
                <input ref={fuelLogoRef} type="file" accept="image/*" className="hidden" onChange={onFuelLogo} data-testid="fuel-station-logo-input" />
                {(company.fuel_stations || []).map((st, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <button type="button" data-testid={`fuel-station-logo-${i}`} title="شعار المحطة"
                      onClick={() => { setFuelLogoIdx(i); fuelLogoRef.current?.click(); }}
                      className="shrink-0 h-9 w-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center overflow-hidden hover:border-blue-400 transition">
                      {fuelUploading === i ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        : st.logo_id ? <img src={fileUrl(st.logo_id)} alt="" className="h-full w-full object-contain" />
                        : <Upload className="h-4 w-4 text-slate-300" />}
                    </button>
                    <Input className="flex-1" placeholder="اسم المحطة" value={st.name || ""} data-testid={`fuel-station-name-${i}`}
                      onChange={(e) => { const a = [...(company.fuel_stations || [])]; a[i] = { ...a[i], name: e.target.value }; set("fuel_stations", a); }} />
                    <Input className="flex-[1.4]" dir="ltr" placeholder="https://maps.google.com/..." value={st.map_url || ""} data-testid={`fuel-station-url-${i}`}
                      onChange={(e) => { const a = [...(company.fuel_stations || [])]; a[i] = { ...a[i], map_url: e.target.value }; set("fuel_stations", a); }} />
                    <button type="button" data-testid={`fuel-station-remove-${i}`}
                      onClick={() => set("fuel_stations", (company.fuel_stations || []).filter((_, j) => j !== i))}
                      className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-white hover:text-rose-600 transition">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                <Button variant="outline" size="sm" className="gap-1.5" data-testid="fuel-station-add"
                  onClick={() => set("fuel_stations", [...(company.fuel_stations || []), { name: "", map_url: "" }])}>
                  <Plus className="h-4 w-4" /> إضافة محطة
                </Button>
                {(company.fuel_stations || []).length === 0 && (
                  <Button variant="ghost" size="sm" className="text-blue-700" data-testid="fuel-station-defaults"
                    onClick={() => set("fuel_stations", [{ name: "سيارة أب", map_url: "" }, { name: "بترو أب", map_url: "" }, { name: "واعي", map_url: "" }])}>
                    إضافة المحطات الافتراضية (سيارة أب، بترو أب، واعي)
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="departments" className="mt-4 space-y-3">
            <div className="flex gap-2">
              <Input value={newDep} onChange={(e) => setNewDep(e.target.value)} placeholder={t("dash.modals.company.newDepPh")}
                data-testid="new-department-input" onKeyDown={(e) => e.key === "Enter" && addDep()} />
              <Button onClick={addDep} className="bg-blue-600 hover:bg-blue-700" data-testid="add-department-btn">
                <Plus className="h-4 w-4 mx-1" /> {t("dash.modals.company.add")}
              </Button>
            </div>
            <div className="space-y-2">
              {deps.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6" data-testid="departments-empty">{t("dash.modals.company.depsEmpty")}</p>
              ) : deps.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-xl border bg-white px-3 py-2" data-testid={`department-${d.id}`}>
                  <div className="flex items-center gap-2 text-slate-700">
                    <span className="font-medium">{d.name}</span>
                    <span className="text-xs text-slate-400 flex items-center gap-1"><Users className="h-3 w-3" /> {t("dash.modals.company.empCount", { n: d.employees })}</span>
                  </div>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeDep(d.id)} data-testid={`delete-department-${d.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="performance" className="mt-4 space-y-4" data-testid="perf-thresholds-tab">
            <div className="flex items-center gap-2 text-slate-700">
              <Gauge className="h-5 w-5 text-blue-600" />
              <p className="font-heading font-bold text-sm">{t("dash.modals.company.perfTitle")}</p>
            </div>
            <p className="text-xs text-slate-500">{t("dash.modals.company.perfDesc")}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">{t("dash.modals.company.minHours")}</label>
                <Input type="number" min={0} step="0.5" dir="ltr" className="mt-1 font-mono-nums" data-testid="perf-min-hours"
                  value={perf.min_hours} onChange={(e) => setPerf((p) => ({ ...p, min_hours: e.target.value }))} />
                <p className="text-[11px] text-slate-400 mt-1">{t("dash.modals.company.minHoursHint")}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">{t("dash.modals.company.maxRejected")}</label>
                <Input type="number" min={0} dir="ltr" className="mt-1 font-mono-nums" data-testid="perf-max-rejected"
                  value={perf.max_rejected} onChange={(e) => setPerf((p) => ({ ...p, max_rejected: e.target.value }))} />
                <p className="text-[11px] text-slate-400 mt-1">{t("dash.modals.company.maxRejectedHint")}</p>
              </div>
            </div>

            {branchNames.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">{t("dash.modals.company.perBranch")}</p>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-sm text-start">
                    <thead className="bg-slate-50 text-slate-500 text-xs">
                      <tr>
                        <th className="px-3 py-2 font-semibold">{t("dash.modals.company.colBranch")}</th>
                        <th className="px-3 py-2 font-semibold">{t("dash.modals.company.colMinHours")}</th>
                        <th className="px-3 py-2 font-semibold">{t("dash.modals.company.colMaxRejected")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {branchNames.map((b) => (
                        <tr key={b} data-testid={`perf-branch-row-${b}`}>
                          <td className="px-3 py-1.5 text-slate-700">{b}</td>
                          <td className="px-3 py-1.5">
                            <Input type="number" min={0} step="0.5" dir="ltr" className="h-8 w-24 font-mono-nums" data-testid={`perf-branch-hours-${b}`}
                              placeholder={String(perf.min_hours)} value={ovr[b]?.min_hours ?? ""}
                              onChange={(e) => setOvrField(b, "min_hours", e.target.value)} />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input type="number" min={0} dir="ltr" className="h-8 w-24 font-mono-nums" data-testid={`perf-branch-rejected-${b}`}
                              placeholder={String(perf.max_rejected)} value={ovr[b]?.max_rejected ?? ""}
                              onChange={(e) => setOvrField(b, "max_rejected", e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400">{t("dash.modals.company.perBranchHint")}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={savePerf} disabled={savingPerf} className="bg-blue-600 hover:bg-blue-700" data-testid="perf-save-btn">
                {savingPerf ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : null} {t("dash.modals.company.savePerf")}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="targets" className="mt-4 space-y-4" data-testid="targets-tab">
            <div className="flex items-center gap-2 text-slate-700">
              <Target className="h-5 w-5 text-blue-600" />
              <p className="font-heading font-bold text-sm">{tl("targetTitle")}</p>
            </div>
            <p className="text-xs text-slate-500">{tl("targetDesc")}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">{tl("orderTarget")}</label>
                <Input type="number" min={0} dir="ltr" className="mt-1 font-mono-nums" data-testid="order-target-input"
                  value={tgt.order_target} onChange={(e) => setTgt((p) => ({ ...p, order_target: e.target.value }))} />
                <p className="text-[11px] text-slate-400 mt-1">{tl("orderTargetHint")}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">{tl("leaveMax")}</label>
                <Input type="number" min={0} dir="ltr" className="mt-1 font-mono-nums" data-testid="leave-max-input"
                  value={tgt.leave_max_per_month} onChange={(e) => setTgt((p) => ({ ...p, leave_max_per_month: e.target.value }))} />
                <p className="text-[11px] text-slate-400 mt-1">{tl("leaveMaxHint")}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">المدة بين الإجازات (بالشهور)</label>
                <Input type="number" min={1} max={60} dir="ltr" className="mt-1 font-mono-nums" data-testid="leave-gap-input"
                  value={tgt.leave_gap_months} onChange={(e) => setTgt((p) => ({ ...p, leave_gap_months: e.target.value }))} />
                <p className="text-[11px] text-slate-400 mt-1">لا يحق للموظف إجازة جديدة إلا بعد مرور هذه المدة من آخر خروج</p>
              </div>
              <div className="flex items-end"><MuqeemLeaveImport /></div>
            </div>
            {branchNames.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">{tl("perBranch")}</p>
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <table className="w-full text-sm text-start">
                    <thead className="bg-slate-50 text-slate-500 text-xs">
                      <tr>
                        <th className="px-3 py-2 font-semibold">{tl("colBranch")}</th>
                        <th className="px-3 py-2 font-semibold">{tl("colTarget")}</th>
                        <th className="px-3 py-2 font-semibold">{tl("colLeave")}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {branchNames.map((b) => (
                        <tr key={b} data-testid={`targets-branch-row-${b}`}>
                          <td className="px-3 py-1.5 text-slate-700">{b}</td>
                          <td className="px-3 py-1.5">
                            <Input type="number" min={0} dir="ltr" className="h-8 w-24 font-mono-nums" data-testid={`target-branch-${b}`}
                              placeholder={String(tgt.order_target)} value={tgtOvr[b] ?? ""}
                              onChange={(e) => setNumOvr(setTgtOvr)(b, e.target.value)} />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input type="number" min={0} dir="ltr" className="h-8 w-24 font-mono-nums" data-testid={`leave-branch-${b}`}
                              placeholder={String(tgt.leave_max_per_month)} value={leaveOvr[b] ?? ""}
                              onChange={(e) => setNumOvr(setLeaveOvr)(b, e.target.value)} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-700">{tl("perVehicle")}</p>
              <div className="overflow-hidden rounded-xl border border-slate-200">
                <table className="w-full text-sm text-start">
                  <thead className="bg-slate-50 text-slate-500 text-xs">
                    <tr>
                      <th className="px-3 py-2 font-semibold">{tl("colVehicle")}</th>
                      <th className="px-3 py-2 font-semibold">{tl("colTarget")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {VEHICLE_TYPES.map((v) => (
                      <tr key={v} data-testid={`targets-vehicle-row-${v}`}>
                        <td className="px-3 py-1.5 text-slate-700">{v}</td>
                        <td className="px-3 py-1.5">
                          <Input type="number" min={0} dir="ltr" className="h-8 w-24 font-mono-nums" data-testid={`target-vehicle-${v}`}
                            placeholder={String(tgt.order_target)} value={vtOvr[v] ?? ""}
                            onChange={(e) => setNumOvr(setVtOvr)(v, e.target.value)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-400">{tl("vehicleHint")}</p>
            </div>
            {branchNames.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">{tl("perBranchVehicle")}</p>
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm text-start">
                    <thead className="bg-slate-50 text-slate-500 text-xs">
                      <tr>
                        <th className="px-3 py-2 font-semibold">{tl("colBranch")}</th>
                        {VEHICLE_TYPES.map((v) => <th key={v} className="px-3 py-2 font-semibold">{v}</th>)}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {branchNames.map((b) => (
                        <tr key={b} data-testid={`targets-bv-row-${b}`}>
                          <td className="px-3 py-1.5 text-slate-700">{b}</td>
                          {VEHICLE_TYPES.map((v) => (
                            <td key={v} className="px-3 py-1.5">
                              <Input type="number" min={0} dir="ltr" className="h-8 w-20 font-mono-nums" data-testid={`target-bv-${b}-${v}`}
                                placeholder={String(vtOvr[v] ?? tgt.order_target)} value={bvOvr[`${b}|${v}`] ?? ""}
                                onChange={(e) => setNumOvr(setBvOvr)(`${b}|${v}`, e.target.value)} />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400">{tl("bvHint")}</p>
              </div>
            )}
            <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4" data-testid="advance-rules-section">
              <div className="flex items-center gap-2 text-slate-700">
                <Banknote className="h-5 w-5 text-amber-600" />
                <p className="font-heading font-bold text-sm">{tl("advTitle")}</p>
              </div>
              <p className="text-xs text-slate-500">{tl("advDesc")}</p>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                <div className="pe-3">
                  <p className="text-sm font-semibold">اشتراط موافقة (توقيع) الموظف على السلفة</p>
                  <p className="text-xs text-slate-500">عند الإيقاف يمكن صرف السلفة المعتمدة مباشرة بدون انتظار توقيع الموظف من التطبيق.</p>
                </div>
                <Switch data-testid="adv-sign-required-switch" checked={!!advSignRequired}
                  className="data-[state=checked]:bg-amber-600" onCheckedChange={setAdvSignRequired} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                <div className="pe-3">
                  <p className="text-sm font-semibold">{tl("advDebtSwitch")}</p>
                  <p className="text-xs text-slate-500">{tl("advDebtHint")}</p>
                </div>
                <Switch data-testid="adv-debt-check-switch" checked={!!advRules.debt_check}
                  className="data-[state=checked]:bg-amber-600"
                  onCheckedChange={(v) => setAdvRules((p) => ({ ...p, debt_check: v }))} />
              </div>
              {advRules.debt_check && (
                <div>
                  <label className="text-sm font-medium text-slate-700">{tl("advDebtLimit")}</label>
                  <Input type="number" min={0} dir="ltr" className="mt-1 font-mono-nums" data-testid="adv-debt-limit-input"
                    value={advRules.debt_limit}
                    onChange={(e) => setAdvRules((p) => ({ ...p, debt_limit: e.target.value }))} />
                </div>
              )}
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                <div className="pe-3">
                  <p className="text-sm font-semibold">{tl("advTargetSwitch")}</p>
                  <p className="text-xs text-slate-500">{tl("advTargetHint")}</p>
                </div>
                <Switch data-testid="adv-target-check-switch" checked={!!advRules.target_check}
                  className="data-[state=checked]:bg-amber-600"
                  onCheckedChange={(v) => setAdvRules((p) => ({ ...p, target_check: v }))} />
              </div>
            </div>
            <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="rider-app-section">
              <div className="flex items-center gap-2 text-slate-700">
                <Gauge className="h-5 w-5 text-indigo-600" />
                <p className="font-heading font-bold text-sm">{tl("riderAppTitle")}</p>
              </div>
              <p className="text-xs text-slate-500">{tl("riderAppDesc")}</p>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                <div className="pe-3">
                  <p className="text-sm font-semibold">{tl("riderPayableSwitch")}</p>
                  <p className="text-xs text-slate-500">{tl("riderPayableHint")}</p>
                </div>
                <Switch data-testid="rider-payable-km-switch" checked={!!riderShowPayableKm}
                  className="data-[state=checked]:bg-indigo-600"
                  onCheckedChange={setRiderShowPayableKm} />
              </div>
              <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                <div className="pe-3">
                  <p className="text-sm font-semibold">السماح للمندوب برفع وثائقه من التطبيق</p>
                  <p className="text-xs text-slate-500">الإقامة والجواز فقط، وكل وثيقة تنتظر اعتماد الإدارة.</p>
                </div>
                <Switch data-testid="portal-doc-upload-switch" checked={!!portalDocUpload}
                  className="data-[state=checked]:bg-indigo-600" onCheckedChange={setPortalDocUpload} />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={saveTgt} disabled={savingTgt} className="bg-blue-600 hover:bg-blue-700" data-testid="targets-save-btn">
                {savingTgt ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : null} {tl("save")}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="approvals" className="mt-4" data-testid="approvals-tab">
            <ApprovalFlowsSettings />
          </TabsContent>
          <TabsContent value="sections" className="mt-4 space-y-3" data-testid="sections-tab">
            <div className="flex items-center gap-2 text-slate-700">
              <ListOrdered className="h-5 w-5 text-blue-600" />
              <p className="font-heading font-bold text-sm">{tl("tabSections")}</p>
            </div>
            <p className="text-xs text-slate-500">{tl("sectionsDesc")}</p>
            {(() => {
              const groups = navLayout.filter((n) => n.t === "group");
              const renderRename = (k, current, testid) => editKey === k ? (
                <span className="flex items-center gap-1">
                  <Input value={editText} onChange={(e) => setEditText(e.target.value)} autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") { setEditKey(null); setEditText(""); } }}
                    className="h-7 w-36 text-sm" data-testid={`rename-input-${testid}`} />
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-600" onClick={commitRename} data-testid={`rename-save-${testid}`}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => { setEditKey(null); setEditText(""); }}><X className="h-4 w-4" /></Button>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span className="text-sm text-slate-700">{current}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-slate-400 hover:text-blue-600" onClick={() => { setEditKey(k); setEditText(current); }} data-testid={`rename-btn-${testid}`}><Pencil className="h-3.5 w-3.5" /></Button>
                </span>
              );
              return (
                <>
                <p className="mb-1.5 flex items-center gap-1 text-[11px] text-slate-500" data-testid="nav-dnd-hint"><GripVertical className="h-3.5 w-3.5" /> اسحب أي قسم أو مجموعة وأفلته في المكان اللي تبيه — أو أفلت القسم على عنوان مجموعة لنقله داخلها.</p>
                <div className="rounded-xl border border-slate-200 divide-y max-h-[48vh] overflow-y-auto" data-testid="nav-organizer">
                  {navLayout.map((node, i) => node.t === "group" ? (
                    <div key={node.id} data-testid={`nav-group-${node.id}`} className={`bg-slate-50/70 transition-colors ${dragOver === `t${i}` ? "ring-2 ring-inset ring-blue-400" : ""}`} {...dnd(`t${i}`, { kind: "top", i }, { kind: "top", i, node })}>
                      <div className={`flex cursor-grab items-center justify-between gap-2 px-3 py-2.5 ${dragOver === `g${node.id}` ? "bg-blue-50" : ""}`} {...dnd(`g${node.id}`, { kind: "group", id: node.id, gi: i }, { kind: "top", i, node })}>
                        <div className="flex items-center gap-2 min-w-0">
                          <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
                          {(() => { const I = resolveGroupIcon(node.icon); return <I className="h-4 w-4 text-blue-600 shrink-0" />; })()}
                          {renderRename(`g:${node.id}`, groupLabel(node, t), `group-${node.id}`)}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <select value={node.icon} onChange={(e) => setGroupIcon(node.id, e.target.value)}
                            className="h-8 rounded-lg border border-slate-200 bg-white text-xs px-1 max-w-[90px]" data-testid={`group-icon-${node.id}`}>
                            {GROUP_ICON_NAMES.map((nm) => <option key={nm} value={nm}>{nm}</option>)}
                          </select>
                          <Button variant="outline" size="icon" className="h-8 w-8" disabled={i === 0} data-testid={`nav-up-${node.id}`} onClick={() => moveTop(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                          <Button variant="outline" size="icon" className="h-8 w-8" disabled={i === navLayout.length - 1} data-testid={`nav-down-${node.id}`} onClick={() => moveTop(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                          <Button variant="outline" size="icon" className="h-8 w-8 text-rose-600 hover:bg-rose-50" data-testid={`nav-delete-group-${node.id}`} onClick={() => deleteGroup(node.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                      <div className="pr-6 pb-1">
                        {node.children.length === 0 ? (
                          <p className="px-3 py-1.5 text-[11px] text-slate-400">لا أقسام — انقل قسمًا هنا من الأسفل.</p>
                        ) : node.children.map((v, ci) => (
                          <div key={v} data-testid={`nav-child-${v}`} className={`flex cursor-grab items-center justify-between gap-2 px-3 py-2 border-r-2 ${dragOver === `c${v}` ? "border-blue-500 bg-blue-50" : "border-slate-200"}`} {...dnd(`c${v}`, { kind: "child", id: node.id, ci, gi: i }, { kind: "child", v })}>
                            <span className="flex min-w-0 items-center gap-1.5"><GripVertical className="h-3.5 w-3.5 shrink-0 text-slate-300" />{renderRename(`l:${v}`, leafLabel(v, navLabels, t), v)}</span>
                            <div className="flex items-center gap-1 shrink-0">
                              <select value="" onChange={(e) => { if (e.target.value) moveValueToGroup(v, e.target.value); }}
                                className="h-7 rounded-lg border border-slate-200 bg-white text-[11px] px-1 max-w-[110px]" data-testid={`move-child-${v}`}>
                                <option value="">نقل لمجموعة…</option>
                                {groups.filter((g) => g.id !== node.id).map((g) => <option key={g.id} value={g.id}>{groupLabel(g, t)}</option>)}
                              </select>
                              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={ci === 0} data-testid={`child-up-${v}`} onClick={() => moveChild(node.id, ci, -1)}><ChevronUp className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={ci === node.children.length - 1} data-testid={`child-down-${v}`} onClick={() => moveChild(node.id, ci, 1)}><ChevronDown className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" title="فك من المجموعة" data-testid={`ungroup-${v}`} onClick={() => ungroupChild(node.id, v)}><CornerUpLeft className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div key={node.v} data-testid={`nav-item-${node.v}`} className={`flex cursor-grab items-center justify-between gap-2 px-3 py-2.5 ${dragOver === `t${i}` ? "bg-blue-50 ring-2 ring-inset ring-blue-400" : ""}`} {...dnd(`t${i}`, { kind: "top", i }, { kind: "top", i, node })}>
                      <span className="flex min-w-0 items-center gap-1.5"><GripVertical className="h-4 w-4 shrink-0 text-slate-300" />{renderRename(`l:${node.v}`, leafLabel(node.v, navLabels, t), node.v)}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <select value="" onChange={(e) => { if (e.target.value) moveValueToGroup(node.v, e.target.value); }}
                          className="h-8 rounded-lg border border-slate-200 bg-white text-[11px] px-1 max-w-[120px]" data-testid={`merge-into-${node.v}`}
                          disabled={!groups.length}>
                          <option value="">دمج في مجموعة…</option>
                          {groups.map((g) => <option key={g.id} value={g.id}>{groupLabel(g, t)}</option>)}
                        </select>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={i === 0} data-testid={`nav-up-${node.v}`} onClick={() => moveTop(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                        <Button variant="outline" size="icon" className="h-8 w-8" disabled={i === navLayout.length - 1} data-testid={`nav-down-${node.v}`} onClick={() => moveTop(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              );
            })()}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={createGroup} data-testid="add-group-btn"><FolderPlus className="h-4 w-4 text-blue-600" /> إنشاء مجموعة</Button>
                <Button variant="outline" size="sm" className="gap-1.5 text-slate-500" onClick={resetNavDefault} data-testid="reset-nav-btn"><RotateCcw className="h-4 w-4" /> استعادة الافتراضي</Button>
              </div>
              <Button onClick={saveNav} disabled={savingNav} className="bg-blue-600 hover:bg-blue-700" data-testid="sections-save-btn">
                {savingNav ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : null} {tl("saveOrder")}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="appearance" className="mt-4 space-y-4" data-testid="appearance-tab">
            <div className="flex items-center gap-2 text-slate-700">
              <Palette className="h-5 w-5" style={{ color: theme.palette?.[600] }} />
              <p className="font-heading font-bold text-sm">{tl("tabAppearance")}</p>
            </div>
            <p className="text-xs text-slate-500">{tl("appearanceDesc")}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="theme-presets">
              {PRESETS.map((p) => {
                const active = theme.id === p.id;
                return (
                  <button key={p.id} type="button" data-testid={`theme-preset-${p.id}`} onClick={() => selectPreset(p)}
                    className={`group rounded-xl border p-3 text-right transition-all ${active ? "border-2 shadow-md" : "border-slate-200 hover:border-slate-300"}`}
                    style={active ? { borderColor: p.palette[600] } : {}}>
                    <div className="flex items-center gap-1.5 mb-2">
                      {[100, 400, 600, 800].map((sh) => (
                        <span key={sh} className="h-6 w-6 rounded-full ring-1 ring-black/5" style={{ backgroundColor: p.palette[sh] }} />
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-700">{p.name}</span>
                      {active && <Check className="h-4 w-4" style={{ color: p.palette[600] }} />}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="rounded-xl border border-slate-200 p-4 space-y-3">
              <p className="text-sm font-medium text-slate-700">{tl("customColor")}</p>
              <div className="flex items-center gap-3">
                <input type="color" value={customColor} onChange={(e) => applyCustom(e.target.value)}
                  data-testid="theme-custom-color" className="h-11 w-14 rounded-lg border border-slate-200 cursor-pointer bg-white p-1" />
                <Input value={customColor} onChange={(e) => applyCustom(e.target.value)} dir="ltr"
                  data-testid="theme-custom-hex" placeholder="#059669" className="h-11 w-32 font-mono-nums text-sm" />
                <div className="flex items-center gap-1.5">
                  {[100, 400, 600, 800].map((sh) => (
                    <span key={sh} className="h-7 w-7 rounded-full ring-1 ring-black/5" style={{ backgroundColor: theme.palette?.[sh] }} />
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-400 mb-3">معاينة مباشرة</p>
              <div className="flex flex-wrap items-center gap-3">
                <button className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-5 py-2.5 transition-colors" data-testid="theme-preview-btn">زر أساسي</button>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-xs font-medium">شارة</span>
                <span className="text-emerald-600 font-semibold text-sm">نص ملوّن</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" className="gap-1.5 text-slate-500" data-testid="theme-reset-btn"
                onClick={() => { setTheme(DEFAULT_THEME); setCustomColor("#059669"); applyTheme(DEFAULT_THEME); }}>
                <RotateCcw className="h-4 w-4" /> استعادة الافتراضي
              </Button>
              <Button onClick={saveTheme} disabled={savingTheme} className="bg-emerald-600 hover:bg-emerald-700" data-testid="theme-save-btn">
                {savingTheme ? <Loader2 className="h-4 w-4 mx-1 animate-spin" /> : null} {tl("saveOrder")}
              </Button>
            </div>
          </TabsContent>

          {notif && (
            <TabsContent value="notifications" className="mt-4">
              <SettingsDialog embedded settings={notif.settings} onSave={notif.onSave} onTest={notif.onTest} onTestEmail={notif.onTestEmail} />
            </TabsContent>
          )}
          {notif && (
            <TabsContent value="biodevice" className="mt-4">
              <BioDeviceSettings />
            </TabsContent>
          )}
        </Tabs>

        {!["notifications", "biodevice"].includes(tab) && <DialogFooter className="gap-2 sticky bottom-0 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 px-4 sm:px-6 py-3 bg-white border-t border-slate-200">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.modals.company.close")}</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="company-save-btn">
            {saving ? t("dash.modals.company.saving") : t("dash.modals.company.saveCompany")}
          </Button>
        </DialogFooter>}
      </DialogContent>
    </Dialog>
  );
};
